import { CommonModule } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
} from 'libphonenumber-js'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import CountryFlagSvg from 'country-list-with-dial-code-and-flag/dist/flag-svg'
import examples from 'libphonenumber-js/examples.mobile.json'
import { NzButtonModule } from 'ng-zorro-antd/button'
import { NzInputModule } from 'ng-zorro-antd/input'
import { NzSelectModule } from 'ng-zorro-antd/select'
import { phoneValidator } from '../../utils/validators/phone-validator'
import { CustomerService } from '../../service/customer.service'
import { map, mergeMap, of } from 'rxjs'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { OnboardingService } from '../../service/onboarding.service'
import { OnboardingProgressState } from '../../gql/generated'
import { Router } from '@angular/router'
import CountryList from 'country-list-with-dial-code-and-flag'

interface Country {
  code: string
  name: string
  dialingCode: string
  flag: SafeHtml
}

@Component({
  selector: 'app-change-phone',
  templateUrl: './change-phone.component.html',
  styleUrls: ['./change-phone.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSelectModule,
    NzInputModule,
    NzButtonModule,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
})
export class ChangePhoneComponent implements OnInit {
  validateForm: FormGroup<{
    countryCode: FormControl<string>
    phone: FormControl<string>
  }>

  placeholder = 'Phone number'
  countryCodes = ['1', '44', '49', '86', '91', '92', '61']
  selectedCountryCode = this.countryCodes[0]
  countryList: Country[] = []

  constructor(
    private fb: NonNullableFormBuilder,
    private customerService: CustomerService,
    private onboardingService: OnboardingService,
    private router: Router,
    private nzNotificationService: NzNotificationService,
    private sanitizer: DomSanitizer
  ) {
    this.validateForm = this.fb.group({
      countryCode: ['', Validators.required],
      // TODO: country code
      // phone: ['', [Validators.required, phoneValidator('countryCode')]],
      phone: ['', [Validators.required]],
    })

    this.validateForm
      .get('countryCode')
      ?.valueChanges.subscribe((dialingCode) => {
        this.setPlaceholder(dialingCode)
      })

    this.setPlaceholder(this.countryCodes[0])
  }

  ngOnInit(): void {
    this.initCountryList()
  }

  setPlaceholder(dialingCode: string): void {
    const isoCountryCode = this.getCountryByDialingCode(dialingCode)
    this.placeholder = isoCountryCode
      ? getExampleNumber(isoCountryCode, examples)?.formatNational() ||
        'Phone number'
      : 'Phone number'
  }

  getCountryByDialingCode(dialingCode: string): CountryCode | undefined {
    return getCountries().find(
      (countryCode) => getCountryCallingCode(countryCode) === dialingCode
    )
  }

  initCountryList() {
    this.countryList = CountryList.getAll()
      .filter((country) => {
        return ['US', 'DE', 'CN', 'KR', 'UK', 'IN'].includes(country.code)
      })
      .map((country) => ({
        code: country.code,
        name: country.name,
        dialingCode: country.dial_code,
        flag: this.sanitizer.bypassSecurityTrustHtml(
          CountryFlagSvg[country.code as never]
        ),
      }))
  }

  getFlagByCode(code: string): SafeHtml {
    const country = this.countryList.find((country) => country.code === code)
    return this.sanitizer.bypassSecurityTrustHtml(
      CountryFlagSvg[country?.code as never]
    )
  }

  getCountryNameByCode(code: string): string {
    const country = this.countryList.find((country) => country.code === code)
    return country?.name || ''
  }

  getDialingCodeByCountryCode(code: string): string {
    const country = this.countryList.find(
      (country) => country.code.toUpperCase() === code.toUpperCase()
    )
    return country?.dialingCode || ''
  }

  onSubmit(): void {
    if (this.validateForm.valid) {
      console.log('Phone number updated:', this.validateForm.value)
      this.customerService
        .getActiveCustomer()
        .pipe(
          mergeMap((response) => {
            return this.customerService
              .updateCustomer({
                phoneNumber: `${this.getDialingCodeByCountryCode(this.validateForm.value.countryCode as string)}${this.validateForm.value.phone}`,
              })
              .pipe(map(() => response))
          }),
          mergeMap((response) => {
            return this.onboardingService.updateOnboarding({
              emailAddress: response?.data.activeCustomer?.emailAddress,
              state: OnboardingProgressState.SMS_VERIFICATION,
            })
          })
        )
        .subscribe((response) => {
          const error = response.data?.updateOnboarding as {
            errorCode?: string
            message?: string
          }
          if (error?.errorCode) {
            this.nzNotificationService.error(
              error.errorCode,
              error.message as string
            )
          } else {
            this.nzNotificationService.success(
              'Updated Successfully',
              'You have successfully updated phone number, please verify your phone number.'
            )
            this.router.navigate(['/verify'])
          }
        })
    }
  }

  getPhoneMaskForCountry(countryCode: string): string {
    if (!countryCode) {
      return '000000000000' // Default mask
    }

    const masks: { [key: string]: string } = {
      US: '(000) 000-0000', // United States: (123) 456-7890
      DE: '0000 0000000', // Germany: 0123 4567890
      CN: '000 0000 0000', // China: 123 4567 8901
      KR: '00-0000-0000', // South Korea: 10-1234-5678
      UK: '00000 000000', // United Kingdom: 01234 567890
      IN: '00000 00000', // India: 12345 67890
    }

    return masks[countryCode.toUpperCase()] || '000000000000' // Default mask if country not found
  }

  unsetLastValue() {
    this.validateForm.get('phone')?.setValue('')
  }
}
